import { type ReactNode } from 'react';
import { ICON_COLOR } from '@zep-us/zus';
import { logger } from '@zep/core-logger';
import { ChevronRightIcon } from '@/icons';
import { useIsInProductContentFrame } from '@/shared/hooks/useIsInProductContentFrame';
import { useOpenUserProfile } from '@/shared/hooks/useOpenUserProfile';
import { LockBadge } from '@/shared/quizList/QuizListItem/LockBadge';
import { cn } from '@/utils/tailwind.util';
interface SubHeaderProps {
  thumbnailUrl: string;
  title: string;
  authorName: string;
  authorId: string;
  isVisible: boolean;
  /** 썸네일 왼쪽에 두는 목록 버튼(걸어온 길의 부모 칸 링크). 카드 안에서만 그린다. */
  parentLink?: ReactNode;
  isLocked: boolean;
}
export const SubHeader = ({
  thumbnailUrl,
  title,
  authorName,
  authorId,
  isVisible,
  parentLink,
  isLocked
}: SubHeaderProps) => {
  // 카드 안에서는 스크롤 영역 맨 위에 붙는다. 카드가 없으면(모바일·외부 플랫폼) 헤더 아래 그대로.
  const inFrame = useIsInProductContentFrame();
  const openUserProfile = useOpenUserProfile();
  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    logger.track('click_user_profile');
    openUserProfile(authorId);
  };
  return <div className={cn('sticky left-0 right-0 top-[50px] z-30 h-0 tablet-sm:top-[58px]', inFrame && 'min-[769px]:top-0')} data-sentry-component="SubHeader" data-sentry-source-file="SubHeader.tsx">
      <div className={cn('h-[56px] border-b border-gray-200 bg-white px-md shadow-md tablet-sm:h-[66px]', inFrame && 'min-[769px]:pl-lg min-[769px]:pr-xl',
    // 숨김일 때 invisible 로 목록 버튼을 Tab 순서에서 뺀다.
    isVisible ? 'translate-y-0 opacity-100' : '-translate-y-full opacity-0 pointer-events-none invisible')}>
        <div className={cn('mx-auto flex h-full max-w-[1024px] items-center gap-lg', inFrame && 'min-[769px]:max-w-none')}>
          {/* Left: Icon + Title */}
          <div className={cn('flex min-w-0 flex-1 items-center gap-xs tablet-sm:gap-lg', inFrame && 'min-[769px]:gap-xs')}>
            {inFrame && parentLink}
            {/* Icon Container */}
            <div className="relative h-xl w-[52px] shrink-0 overflow-hidden rounded-sm tablet-sm:h-[42px] tablet-sm:w-3xl">
              <img src={thumbnailUrl} alt={title} className="h-full w-full object-cover" />
              {isLocked && <LockBadge className="left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 p-2xs" iconSize="2xs" iconClassName="tablet-sm:size-md" />}
            </div>

            {/* Title */}
            <h2 className="min-w-0 flex-1 truncate text-body-lg-bold text-default tablet-sm:text-heading-sm-bold">
              {title}
            </h2>
          </div>

          {/* Right: Author */}
          <div className="flex shrink-0 items-center gap-xs">
            <div className="flex items-center gap-4xs">
              <span className="cursor-pointer text-body-md-semibold text-neutral hover:text-assistive" onClick={handleClick}>
                {authorName}
              </span>
              <div className="flex h-4 w-4 items-center justify-center">
                <ChevronRightIcon size="2xs" fill={ICON_COLOR.neutral} data-sentry-element="ChevronRightIcon" data-sentry-source-file="SubHeader.tsx" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>;
};